import { Bar } from "vue-chartjs";
import { trackedItemCardGraphOptions } from "./trackedItemCardGraphOptions.js";
import ToggleWeightCost from "@/components/toggleWeightCost/toggleWeightCost.vue";
import { createNamespacedHelpers } from "vuex";

const { mapGetters: brandingGetters } = createNamespacedHelpers("branding"),
	{ mapGetters: userGetters } = createNamespacedHelpers("user"),
	DECIMALS = 2,
	BAR_W = 24;

export default {
	extends: Bar,

	components: { ToggleWeightCost },

	props: {
		data: {
			type: Object,
			required: true,
		},
	},

	data() {
		return {
			datasets: null,
			isCostToggleActive: false,
		};
	},

	computed: {
		...userGetters(["activeOutlet", "weightUnitMinor", "weightFactorMinor"]),
		...brandingGetters(["colors"]),
	},

	mounted() {
		this.addPlugin({ beforeTooltipDraw: drawActionTipPlugin });

		this.renderGraph();
	},

	methods: {
		renderGraph,
		chartData,
		getLabel,
		getChartYAxisLabel,
		getChartTooltip,
		getTextWidth(text) {
			// canvas should be garbage collected automatically
			const parentElement = this.$refs["canvas-container"],
				computedStyle = window.getComputedStyle(parentElement),
				canvas = document.createElement("canvas"),
				context = canvas.getContext("2d");

			context.font = computedStyle.font;
			return context.measureText(text).width;
		},
	},

	watch: {
		isCostToggleActive: function () {
			this.renderGraph();
		},
	},
};

function ceil(value) {
	const HUNDRED = 100;
	return Math.ceil(value * HUNDRED) / HUNDRED;
}

function renderGraph() {
	/*
	 * find the width difference between the two highest labels (weight, cost)
	 * and compensate for it...
	 */
	const maxCost = ceil(Math.max(...this.chartData(true).datasets[0].data)),
		maxWeight = ceil(Math.max(...this.chartData(false).datasets[0].data)),
		maxCostLabel = this.getTextWidth(this.getLabel(maxCost, true)),
		maxWeightLabel = this.getTextWidth(this.getLabel(maxWeight, false)),
		costPadding = Math.max(maxWeightLabel - maxCostLabel, 0),
		weightPadding = Math.max(maxCostLabel - maxWeightLabel, 0),
		frontPadding = this.isCostToggleActive ? costPadding : weightPadding,
		data = this.chartData(this.isCostToggleActive),
		options = trackedItemCardGraphOptions();

	this.$refs["canvas-container"].style.paddingLeft = `${frontPadding}px`;

	options.scales.yAxes[0].ticks.callback = this.getChartYAxisLabel;
	options.goalWeight = this.goalWeight;
	options.goalColor = this.colors[1];
	options.tooltips.callbacks.label = this.getChartTooltip;

	this.renderChart(data, options);
}

function drawActionTipPlugin(chart) {
	const ctxPlugin = chart.ctx,
		xAxe = chart.scales[chart.options.scales.xAxes[0].id],
		yAxe = chart.scales[chart.options.scales.yAxes[0].id],
		/*
		 * yAxe.top = position of yAxe.max
		 * yAxe.top + yAxe.height = position of yAxe.min
		 * goal relative position (grp) = height * (goal - min) / (max - min)
		 * goal position = top + height - grp
		 * console.log(yAxe.min, yAxe.max, yAxe.top, yAxe.height);
		 */
		goalWeight = chart.options.goalWeight,
		lineAt = yAxe.top + yAxe.height * (1 - (goalWeight - yAxe.min) / (yAxe.max - yAxe.min));

	// console.log('xAxe:', xAxe, xAxe.min, xAxe.max);
	ctxPlugin.strokeStyle = chart.options.goalColor;
	ctxPlugin.lineWidth = 2;
	ctxPlugin.beginPath();
	ctxPlugin.moveTo(xAxe.left, lineAt);
	ctxPlugin.lineTo(xAxe.right, lineAt);
	ctxPlugin.stroke();
}

/* eslint-disable complexity, camelcase */
function chartData(cost) {
	const data = this.data,
		tickFormat = "MMM YYYY",
		baseline = cost ? data.baseline.cpg : data.baseline.wpg * this.weightFactorMinor,
		performance = [baseline],
		labels = [this.$t("global.baseline")],
		_self = this;

	data.months.forEach((month) => {
		labels.push(this.$moment(month.year_month).format(tickFormat) + (month.has_action ? " ★" : ""));
		performance.push(cost ? month.cpg : month.wpg * this.weightFactorMinor);
	});

	this.datasets = [
		{
			barThickness: BAR_W,
			maxBarThickness: BAR_W,
			label: this.$t("tracked_items.your_performance"),
			backgroundColor(context) {
				// color the first item - baseline - differently
				return context.dataIndex === 0 ? _self.colors[4] : _self.colors[3];
			},
			data: performance,
		},
	];

	this.goalWeight = cost ? data.cost.monthly_goal_pg : data.weight.monthly_goal_pg * this.weightFactorMinor;

	return {
		labels,
		datasets: this.datasets,
	};
}

function getLabel(value, cost) {
	if (cost) return `${this.activeOutlet.currency} ${value.toFixed(DECIMALS)}`;

	return `${value.toFixed(DECIMALS)} ${this.weightUnitMinor}`;
}

function getChartYAxisLabel(value) {
	// note: two spaces at the end are by design, to add a bit of padding
	return `${this.getLabel(value, this.isCostToggleActive)}  `;
}

function getChartTooltip(tooltipItem) {
	if (this.isCostToggleActive) {
		const cost = Number(tooltipItem.value).toFixed(DECIMALS);
		return `${this.activeOutlet.currency} ${cost}`;
	}

	const weight = Number(tooltipItem.value).toFixed(DECIMALS);
	return `${weight} ${this.weightUnitMinor}`;
}
